import { Extension, InsertDriveFile, Security, Settings } from '@mui/icons-material'
import CommuteIcon from '@mui/icons-material/CommuteOutlined'
import { TFunction } from 'react-i18next'
import { AiFillBank } from 'react-icons/ai'
import {
  BiBarChartSquare,
  BiCar,
  BiDollarCircle,
  BiGitBranch,
  BiLayer,
  BiMap,
  BiSpreadsheet,
  BiTrip,
  BiUser,
  BiDoughnutChart,
} from 'react-icons/bi'
import { BsDatabaseFillCheck } from 'react-icons/bs'
import { CiDeliveryTruck } from 'react-icons/ci'
import { FaCarAlt, FaLeaf, FaUser, FaParking } from 'react-icons/fa'
import { FaRoad } from 'react-icons/fa6'
import { GrTest } from 'react-icons/gr'
import { MdHealthAndSafety, MdModeOfTravel } from 'react-icons/md'
import { PiMapPinLineBold } from 'react-icons/pi'
import { TbBike } from 'react-icons/tb'

import {
  AgencySettings_EnabledFeaturesEnum,
  DomainPermission_DomainEnum,
  DomainPermission_VerbEnum,
  Favorite_PageIdEnum,
} from './api/gen'
import { IPage } from './auth/RouteAuthWrapper'
import UberLyftIcon from './commons/icons/UberLyftIcon'
import LazyAreas from './pages/Areas/LazyAreas'
import LazyAuditLogs from './pages/AuditLogs/LazyAuditLogs'
import LazyMds from './pages/extensions/Mds/LazyMds'
import FeeRatesList from './pages/FeeRates/FeeRatesList'
import LazyFeeRates from './pages/FeeRates/LazyFeeRates'
import FeeSummary from './pages/FeeRates/SummaryPage/FeeSummaryPage'
import Violations from './pages/MdsViolations/MdsViolations'
import ViolationMap from './pages/MdsViolations/ViolationMap'
import LazyPolicies from './pages/Policies/LazyPolicies'
import PenaltySchemes from './pages/Policies/PenaltySchemes'
import PolicyKPI from './pages/Policies/PolicyKPI'
import SmartPatrolRouter from './pages/Policies/SmartPatrol'
import LazyPowerBICurbOverviewReport from './pages/PowerBI/LazyPowerBICurbOverviewReport'
import LazyPowerBIDataQualityKpisReport from './pages/PowerBI/LazyPowerBIDataQualityKpisReport'
import LazyPowerBIGBFSReport from './pages/PowerBI/LazyPowerBIDockedBikesReport'
import LazyPowerBIEnvironmentKpisReport from './pages/PowerBI/LazyPowerBIEnvironmentKpisReport'
import LazyPowerBIHeatmapReport from './pages/PowerBI/LazyPowerBIHeatmapReport'
import LazyPowerBiParkingBayReport from './pages/PowerBI/LazyPowerBiParkingBayReport'
import LazyPowerBIPassengersServicesTestReport from './pages/PowerBI/LazyPowerBIPassengersServicesTestReport'
import LazyPowerBIRideHailingReport from './pages/PowerBI/LazyPowerBIRideHailingReport'
import LazyPowerBISafetyReport from './pages/PowerBI/LazyPowerBISafetyReport'
import LazyPowerBISmartLoadingZoneReport from './pages/PowerBI/LazyPowerBISmartLoadingZoneReport'
import LazyPowerBITripsKpisReport from './pages/PowerBI/LazyPowerBITripsKpisReport'
import LazyPowerBIUtilizationKpisReport from './pages/PowerBI/LazyPowerBIUtilizationKpisReport'
import LazyPowerBIVehiclesKpisReport from './pages/PowerBI/LazyPowerBIVehiclesKpisReport'
import LazyProviders from './pages/Providers/LazyProviders'
import LazyReports from './pages/Reports/LazyReports'
import LazySupervisionMap from './pages/SupervisionMap/LazySupervisionMap'
import LazyTripList from './pages/Trips/LazyTripList'
import LazyTrips from './pages/Trips/LazyTrips'
import LazyTripUserKpis from './pages/Trips/LazyTripUserKpis'
import TripHeatmaps from './pages/Trips/TripHeatmap/TripHeatmap'
import TripsEnvKPI from './pages/Trips/TripsEnvKPI'
import TripsKPI from './pages/Trips/TripsKPI'
import TripsTable from './pages/Trips/TripsTable'
import UsersKPI from './pages/Trips/UsersKPI'
import LazyVehicles from './pages/Vehicles/LazyVehicles'
import LazyVehiclesList from './pages/Vehicles/LazyVehiclesList'
import VehicleDeploymentCharts from './pages/Vehicles/VehicleDeploymentCharts'
import VehicleList from './pages/Vehicles/VehicleList'
import VehicleUsageCharts from './pages/Vehicles/VehicleUsageCharts'
import SETTINGS from './settings'

export default (t: TFunction<'translation', undefined>): Partial<IPage>[] => [
  {
    label: t('Mobility'),
    Icon: CommuteIcon,
    pages: [
      {
        pageID: Favorite_PageIdEnum.map,
        label: t('menu.label.map'),
        to: '/map',
        Icon: BiMap,
        permissions: [[DomainPermission_VerbEnum.read, DomainPermission_DomainEnum.map]],
        component: LazySupervisionMap,
      },
      {
        pageID: Favorite_PageIdEnum.vehicles,
        label: t('menu.label.vehicles'),
        to: '/vehicles',
        Icon: BiCar,
        permissions: [[DomainPermission_VerbEnum.read, DomainPermission_DomainEnum.device]],
        component: LazyVehicles,
        enabled:
          SETTINGS.app.fleet_enable_vehicle_list ||
          SETTINGS.app.fleet_enable_deployment_kpi ||
          SETTINGS.app.fleet_enable_utilization_kpi,
        pages: [
          {
            pageID: Favorite_PageIdEnum.vehicles_list,
            label: t('Vehicle list'),
            to: '/vehicles',
            permissions: [[DomainPermission_VerbEnum.read, DomainPermission_DomainEnum.device]],
            component: VehicleList,
            enabled: SETTINGS.app.fleet_enable_vehicle_list,
          },
          {
            pageID: Favorite_PageIdEnum.vehicles_deployment_KPI,
            label: t('Deployment KPI'),
            to: '/vehicles/deployment',
            permissions: [[DomainPermission_VerbEnum.summarize, DomainPermission_DomainEnum.device]],
            component: VehicleDeploymentCharts,
            enabled: SETTINGS.app.fleet_enable_deployment_kpi,
          },
          {
            pageID: Favorite_PageIdEnum.vehicles_utilization_KPI,
            label: t('Usage KPI'),
            to: '/vehicles/usage',
            permissions: [[DomainPermission_VerbEnum.summarize, DomainPermission_DomainEnum.device]],
            component: VehicleUsageCharts,
            enabled: SETTINGS.app.fleet_enable_utilization_kpi,
          },
        ],
      },
      {
        pageID: Favorite_PageIdEnum.trips,
        label: t('menu.label.trips'),
        to: '/trips',
        Icon: BiTrip,
        permissionsUnion: [
          [DomainPermission_VerbEnum.read, DomainPermission_DomainEnum.trip],
          [DomainPermission_VerbEnum.summarize, DomainPermission_DomainEnum.trip],
        ],
        component: LazyTrips,
        enabled:
          SETTINGS.app.trips_enable_environment_kpi ||
          SETTINGS.app.trips_enable_heatmap ||
          SETTINGS.app.trips_enable_trip_kpi ||
          SETTINGS.app.trips_enable_trips_list ||
          SETTINGS.app.trips_enable_users_kpi,
        pages: [
          {
            pageID: Favorite_PageIdEnum.trips_list,
            label: t('trips.list'),
            to: '/trips/list',
            permissions: [[DomainPermission_VerbEnum.read, DomainPermission_DomainEnum.trip]],
            component: TripsTable,
            enabled: SETTINGS.app.trips_enable_trips_list,
          },
          {
            pageID: Favorite_PageIdEnum.trips_heatmap,
            label: t('trips.heatmap'),
            to: '/trips/heatmap',
            permissions: [[DomainPermission_VerbEnum.summarize, DomainPermission_DomainEnum.trip]],
            featureFlags: [
              AgencySettings_EnabledFeaturesEnum.heatmap_trips,
              AgencySettings_EnabledFeaturesEnum.heatmap_origin_destination,
            ],
            component: TripHeatmaps,
            enabled: SETTINGS.app.trips_enable_heatmap,
          },
          {
            pageID: Favorite_PageIdEnum.trips_KPI,
            label: t('Trips KPI'),
            to: '/trips/kpi',
            permissions: [[DomainPermission_VerbEnum.summarize, DomainPermission_DomainEnum.trip]],
            component: TripsKPI,
            enabled: SETTINGS.app.trips_enable_trip_kpi,
          },
          {
            pageID: Favorite_PageIdEnum.trips_env_KPI,
            label: t('Environment KPI'),
            to: '/trips/environment',
            permissions: [[DomainPermission_VerbEnum.summarize, DomainPermission_DomainEnum.trip]],
            featureFlags: [AgencySettings_EnabledFeaturesEnum.environment_kpi],
            component: TripsEnvKPI,
            enabled: SETTINGS.app.trips_enable_environment_kpi,
          },
          {
            pageID: Favorite_PageIdEnum.trips_user_KPI,
            label: t('Users KPI'),
            to: '/trips/users',
            permissions: [[DomainPermission_VerbEnum.summarize, DomainPermission_DomainEnum.trip]],
            featureFlags: [AgencySettings_EnabledFeaturesEnum.user_kpi],
            component: UsersKPI,
            enabled: SETTINGS.app.trips_enable_users_kpi,
          },
        ],
      },
      {
        pageID: Favorite_PageIdEnum.providers,
        label: t('menu.label.providers'),
        to: '/providers',
        Icon: BiUser,
        permissions: [[DomainPermission_VerbEnum.read, DomainPermission_DomainEnum.provider]],
        component: LazyProviders,
      },
      {
        pageID: Favorite_PageIdEnum.ride_hailing,
        label: t('menu.label.uber_lyft'),
        to: '/ride_hailing',
        Icon: UberLyftIcon,
        permissions: [[DomainPermission_VerbEnum.read, DomainPermission_DomainEnum.ride_hailing]],
        component: LazyPowerBIRideHailingReport,
      },
      {
        pageID: Favorite_PageIdEnum.passengers_services_test,
        label: t('menu.label.passenger_service_test'),
        to: '/passenger-service-test',
        Icon: GrTest,
        permissions: [[DomainPermission_VerbEnum.read, DomainPermission_DomainEnum.passengers_services_test]],
        component: LazyPowerBIPassengersServicesTestReport,
      },
      {
        pageID: Favorite_PageIdEnum.safety,
        label: t('menu.label.safety_overview'),
        to: '/safety',
        Icon: MdHealthAndSafety,
        permissions: [[DomainPermission_VerbEnum.read, DomainPermission_DomainEnum.safety]],
        component: LazyPowerBISafetyReport,
      },
      {
        pageID: Favorite_PageIdEnum.docked_bikes,
        label: t('menu.label.docked_bikes'),
        to: '/docked_bikes',
        Icon: TbBike,
        permissions: [[DomainPermission_VerbEnum.read, DomainPermission_DomainEnum.docked_bikes]],
        component: LazyPowerBIGBFSReport,
      },
      {
        pageID: Favorite_PageIdEnum.vehicles_kpis,
        label: t('menu.label.vehicles_kpis'),
        to: '/vehicles_kpis',
        Icon: FaCarAlt,
        permissions: [[DomainPermission_VerbEnum.read, DomainPermission_DomainEnum.vehicles_kpis]],
        component: LazyPowerBIVehiclesKpisReport,
      },
      {
        pageID: Favorite_PageIdEnum.utilization_kpis,
        label: t('menu.label.utilization_kpis'),
        to: '/utilization_kpis',
        Icon: BiDoughnutChart,
        permissions: [[DomainPermission_VerbEnum.read, DomainPermission_DomainEnum.utilization_kpis]],
        component: LazyPowerBIUtilizationKpisReport,
      },
      {
        pageID: Favorite_PageIdEnum.trips_kpis,
        label: t('menu.label.trips_kpis'),
        to: '/trips_kpis',
        Icon: MdModeOfTravel,
        permissions: [[DomainPermission_VerbEnum.read, DomainPermission_DomainEnum.trips_kpis]],
        component: LazyPowerBITripsKpisReport,
      },
      {
        pageID: Favorite_PageIdEnum.heatmaps,
        label: t('menu.label.heatmaps'),
        to: '/heatmaps',
        Icon: PiMapPinLineBold,
        component: LazyPowerBIHeatmapReport,
        pages: [
          {
            pageID: Favorite_PageIdEnum.heatmaps,
            label: t('heatmaps.heatmap'),
            to: '/heatmaps',
            permissions: [[DomainPermission_VerbEnum.read, DomainPermission_DomainEnum.heatmaps]],
            component: LazyPowerBIHeatmapReport,
          },
          {
            pageID: Favorite_PageIdEnum.heatmaps_street_segment,
            label: t('heatmaps.street_segment'),
            to: '/heatmaps/street_segment',
            permissionsUnion: [
              [DomainPermission_VerbEnum.summarize, DomainPermission_DomainEnum.trip],
              [DomainPermission_VerbEnum.read, DomainPermission_DomainEnum.street_segment],
            ],
            component: TripHeatmaps,
          },
        ],
      },
      {
        pageID: Favorite_PageIdEnum.environment_kpis,
        label: t('menu.label.environment_kpis'),
        to: '/environment_kpis',
        Icon: FaLeaf,
        permissions: [[DomainPermission_VerbEnum.read, DomainPermission_DomainEnum.environment_kpis]],
        component: LazyPowerBIEnvironmentKpisReport,
      },
      {
        pageID: Favorite_PageIdEnum.data_quality_kpis,
        label: t('menu.label.data_quality_kpis'),
        to: '/data_quality_kpis',
        Icon: BsDatabaseFillCheck,
        permissions: [[DomainPermission_VerbEnum.read, DomainPermission_DomainEnum.data_quality_kpis]],
        component: LazyPowerBIDataQualityKpisReport,
      },
      {
        pageID: Favorite_PageIdEnum.smart_loading_zone,
        label: t('menu.label.smart_loading_zone'),
        to: '/smart_loading_zone',
        Icon: CiDeliveryTruck,
        permissions: [[DomainPermission_VerbEnum.read, DomainPermission_DomainEnum.smart_loading_zone]],
        component: LazyPowerBISmartLoadingZoneReport,
      },
      {
        pageID: Favorite_PageIdEnum.curb_overview,
        label: t('menu.label.curb_overview'),
        to: '/curb_overview',
        Icon: FaRoad,
        permissions: [[DomainPermission_VerbEnum.read, DomainPermission_DomainEnum.curb_overview]],
        component: LazyPowerBICurbOverviewReport,
      },
      {
        pageID: Favorite_PageIdEnum.vehicles_list_standalone,
        label: t('menu.label.vehicles_list'),
        to: '/vehicles_list',
        Icon: BiCar,
        permissions: [['read', 'device']],
        component: LazyVehiclesList,
        enabled: SETTINGS.app.enable_vehicles_list,
      },
      {
        pageID: Favorite_PageIdEnum.trips_list_standalone,
        label: t('menu.label.trip_list'),
        to: '/trip_list',
        Icon: BiTrip,
        permissions: [['read', 'trip']],
        component: LazyTripList,
        enabled: SETTINGS.app.enable_trip_list,
      },
      {
        pageID: Favorite_PageIdEnum.trips_user_KPI_standalone,
        label: t('menu.label.trip_user_kpi'),
        to: '/trip_user_kpi',
        Icon: FaUser,
        permissions: [['summarize', 'trip']],
        component: LazyTripUserKpis,
        enabled: SETTINGS.app.enable_trip_user_kpis,
      },
    ],
  },
  {
    label: t('Reports'),
    Icon: InsertDriveFile,
    permissions: [[DomainPermission_VerbEnum.read, DomainPermission_DomainEnum.report]],
    pages: [
      {
        pageID: Favorite_PageIdEnum.reports,
        label: t('Historical & Area reports'),
        to: '/reports',
        Icon: BiBarChartSquare,
        permissions: [[DomainPermission_VerbEnum.read, DomainPermission_DomainEnum.report]],
        component: LazyReports,
      },
    ],
  },
  {
    label: t('Regulation'),
    Icon: Security,
    pages: [
      {
        pageID: Favorite_PageIdEnum.fee_rates,
        label: t('Operator Fees'),
        to: '/fee_rates',
        Icon: BiDollarCircle,
        permissions: [[DomainPermission_VerbEnum.read, DomainPermission_DomainEnum.fee_rates]],
        component: LazyFeeRates,
        pages: [
          {
            pageID: Favorite_PageIdEnum.fee_rates_list,
            label: t('Fee rates list'),
            to: '/fee_rates',
            permissions: [[DomainPermission_VerbEnum.read, DomainPermission_DomainEnum.fee_rates]],
            component: FeeRatesList,
          },
          {
            pageID: Favorite_PageIdEnum.fee_scheme,
            label: t('Fee rates summary'),
            to: '/fee_rates/summary',
            permissions: [[DomainPermission_VerbEnum.read, DomainPermission_DomainEnum.fee_rates]],
            component: FeeSummary,
          },
        ],
      },
      {
        pageID: Favorite_PageIdEnum.policies,
        label: t('Policies'),
        to: '/policies',
        Icon: AiFillBank,
        permissions: [
          [DomainPermission_VerbEnum.read, DomainPermission_DomainEnum.policy],
          [DomainPermission_VerbEnum.read, DomainPermission_DomainEnum.area],
        ],
        component: LazyPolicies,
        pages: [
          {
            pageID: Favorite_PageIdEnum.policies_list,
            label: t('Policy list'),
            to: '/policies',
            permissions: [[DomainPermission_VerbEnum.read, DomainPermission_DomainEnum.policy]],
            component: Violations,
          },
          {
            pageID: Favorite_PageIdEnum.policies_violations,
            label: t('Violations'),
            to: '/policies/offenses',
            permissions: [[DomainPermission_VerbEnum.read, DomainPermission_DomainEnum.policy]],
            component: TripHeatmaps,
          },
          {
            pageID: Favorite_PageIdEnum.policies_smart_patrol,
            label: t('Smart Patrol'),
            to: '/policies/smart_patrol',
            permissions: [[DomainPermission_VerbEnum.read, DomainPermission_DomainEnum.smart_patrol]],
            component: SmartPatrolRouter,
          },
          {
            pageID: Favorite_PageIdEnum.policies_violation_map,
            label: t('Violation map'),
            to: '/policies/violation_map',
            permissions: [[DomainPermission_VerbEnum.read, DomainPermission_DomainEnum.policy]],
            featureFlags: [AgencySettings_EnabledFeaturesEnum.violation_map],
            component: ViolationMap,
          },
          {
            pageID: Favorite_PageIdEnum.policies_penalty_scheme,
            label: t('Penalty schemes'),
            to: '/policies/penalty_schemes',
            permissions: [[DomainPermission_VerbEnum.read, DomainPermission_DomainEnum.policy]],
            component: PenaltySchemes,
          },
          {
            pageID: Favorite_PageIdEnum.policies_kpi,
            label: t('Policies KPI'),
            to: '/policies/kpi',
            permissions: [[DomainPermission_VerbEnum.summarize, DomainPermission_DomainEnum.policy]],
            component: PolicyKPI,
          },
        ],
      },
      {
        pageID: Favorite_PageIdEnum.parking_bay,
        label: t('menu.label.parking_bay'),
        to: '/parking_bay',
        Icon: FaParking,
        permissions: [[DomainPermission_VerbEnum.read, DomainPermission_DomainEnum.parking_bay]],
        component: LazyPowerBiParkingBayReport,
      },
    ],
  },
  {
    label: t('Connectors'),
    Icon: Extension,
    pages: [
      {
        pageID: Favorite_PageIdEnum.mds,
        label: t('MDS'),
        to: '/extensions/mds',
        Icon: BiGitBranch,
        permissions: [[DomainPermission_VerbEnum.read, DomainPermission_DomainEnum.mds]],
        component: LazyMds,
      },
    ],
  },
  {
    label: t('Settings'),
    Icon: Settings,
    pages: [
      {
        pageID: Favorite_PageIdEnum.areas,
        label: t('Areas'),
        to: '/areas',
        Icon: BiLayer,
        permissions: [[DomainPermission_VerbEnum.read, DomainPermission_DomainEnum.area]],
        component: LazyAreas,
      },
      {
        pageID: Favorite_PageIdEnum.logs,
        label: t('Audit Logs'),
        to: '/logs',
        Icon: BiSpreadsheet,
        permissions: [[DomainPermission_VerbEnum.read, DomainPermission_DomainEnum.administration]],
        component: LazyAuditLogs,
      },
    ],
  },
]
